<script setup lang="ts">
import { useSpeechBubbleStore } from '~/stores/speechBubble'

const props = defineProps<{
  isLogoCollapsed: boolean
}>()

const emit = defineEmits(['logoCollapse'])

const { logoUrl, logoAriaLabel } = useLogoLink()

const events = ['click', 'mousemove', 'keydown', 'touchstart', 'scroll']

let logoCollapseTimeout: ReturnType<typeof setTimeout>

const speechBubbleStore = useSpeechBubbleStore()

// Computed to determine if logo bubble should show
const showLogoBubble = computed(() => {
  // Always show at logo when position is 'logo'
  if (speechBubbleStore.position === 'logo') return true

  // For 'search' position, only show on mobile (below tablet breakpoint)
  if (speechBubbleStore.position === 'search') {
    // We'll use CSS to handle this with display: none on desktop
    return true
  }

  return false
})

function addListeners() {
  if (props.isLogoCollapsed)
    return

  events.forEach(event => document.addEventListener(event, handleLogoCollapse))
  logoCollapseTimeout = setTimeout(() => handleLogoCollapse(), 4000)
}

function removeListeners() {
  events.forEach(event => document.removeEventListener(event, handleLogoCollapse))
  clearTimeout(logoCollapseTimeout)
}

function handleLogoCollapse() {
  emit('logoCollapse')
  removeListeners()
}

onMounted(() => addListeners())

onUnmounted(() => removeListeners())
</script>

<template>
  <div
    class="logo-container"
  >
    <NuxtLink
      class="logo-link"
      :class="{ collapsed: isLogoCollapsed }"
      :to="logoUrl"
      :aria-label="logoAriaLabel === 'cart' ? $t(logoAriaLabel) : logoAriaLabel"
    >
      <img
        class="logo-icon"
        src="/icons/logo-hs.svg"
        alt=""
      >
      <HeaderSpeechBubble
        v-show="showLogoBubble"
        position="logo"
        class="logo-bubble"
        :class="{ 'search-position': speechBubbleStore.position === 'search' }"
      />
      <div
        class="logo-full-container"
        :class="{ collapsed: isLogoCollapsed }"
      >
        <img
          class="logo-full"
          :class="{ collapsed: isLogoCollapsed }"
          src="/icons/logo-hs-large.svg"
          height="10"
          alt=""
        >
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.logo-icon {
  width: 26px;
  height: 24px;
}

.logo-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  transition: justify-content 0.5s ease-in-out;

  &.collapsed {
    @media screen and (min-width: $tablet) {
      justify-content: center;
    }
  }
}

.logo-full-container {
  overflow: hidden;
  width: 126px;
  margin-left: 1.2rem;
  transition: all 0.5s ease-out;

  &.collapsed {
    width: 0;
    margin-left: 0;
  }

  @media screen and (min-width: $tablet) and (max-width: $desktop) {
    display: none;
  }
}

.logo-full {
  transition: all 0.5s ease-out;
  opacity: 100;

  &.collapsed {
    opacity: 0;
    margin-left: 0;
    transform: translateX(-10rem);
  }
}

.logo-bubble.search-position {
  @media screen and (min-width: $tablet) {
    display: none;
  }
}
</style>
